import React, {useMemo, useState} from 'react';
import {CButton} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {EventInput} from '@fullcalendar/common';
import {EtapaProcesso, Evento, Processo, SituacaoEvento, dataBr, dataHoraApi, isoLocal, resolvido} from "./modelo";
import Calendario, {COR_EVENTO, COR_PRAZO, COR_PRAZO_ATRASADO} from "./calendario";
import Remarcacao, {PedidoRemarcacao, pedidoDoCalendario} from "./remarcacao";

export interface NovoAgendamento {
    data?: string,
    inicio?: string,
    fim?: string,
    itemId?: number | null,
    etapaId?: number | null,
}

interface Props {
    processo: Processo,
    /** Gestor, master ou quem ocupa algum papel (a API confere de novo). */
    podeAgendar: boolean,
    podeReprogramar: (etapa: EtapaProcesso) => boolean,
    recarregar: () => void,
    onAbrirItem: (itemId: number) => void,
    onAgendar: (novo: NovoAgendamento) => void,
    onAbrirEvento: (evento: Evento) => void,
}

/**
 * Agenda do processo (#480058): eventos (kick-off, reunioes, treinamentos,
 * entregas, go-live) e, se quiser, os prazos dos itens pendentes. Ao lado, os
 * proximos eventos. Arrastar remarca o evento ou reprograma o prazo do item;
 * selecionar um horario agenda (#484909).
 */
const AgendaAba = ({processo, podeAgendar, podeReprogramar, recarregar, onAbrirItem, onAgendar, onAbrirEvento}: Props) => {
    const [comPrazos, setComPrazos] = useState(false);
    const [remarcando, setRemarcando] = useState<PedidoRemarcacao | null>(null);
    const eventos = processo.eventos || [];
    const ativo = processo.situacao === 1 || processo.situacao === 2;

    const noCalendario: EventInput[] = useMemo(() => {
        const lista: EventInput[] = eventos.map(evento => {
            const agendado = evento.situacao === SituacaoEvento.AGENDADO;
            return {
                id: `e${evento.id}`,
                title: evento.item_titulo && evento.item_titulo !== evento.titulo ? `${evento.titulo} · ${evento.item_titulo}` : evento.titulo,
                start: isoLocal(evento.inicio),
                end: evento.fim ? isoLocal(evento.fim) : undefined,
                backgroundColor: evento.situacao === SituacaoEvento.CANCELADO ? '#e5e7eb' : COR_EVENTO[evento.tipo],
                borderColor: evento.situacao === SituacaoEvento.CANCELADO ? '#e5e7eb' : COR_EVENTO[evento.tipo],
                textColor: evento.situacao === SituacaoEvento.CANCELADO ? '#6b7280' : '#fff',
                classNames: [`sit-evento-${evento.situacao}`],
                startEditable: ativo && podeAgendar && agendado,
                durationEditable: ativo && podeAgendar && agendado,
                extendedProps: {evento},
            };
        });
        if (comPrazos) {
            processo.etapas.forEach(etapa => etapa.itens.forEach(item => {
                if (resolvido(item)) return;
                lista.push({
                    id: `i${item.id}`,
                    title: `Prazo: ${item.titulo}`,
                    start: item.prazo,
                    allDay: true,
                    backgroundColor: 'transparent',
                    borderColor: item.atrasado ? COR_PRAZO_ATRASADO : COR_PRAZO,
                    textColor: item.atrasado ? COR_PRAZO_ATRASADO : '#475569',
                    classNames: ['pr-cal-prazo'],
                    startEditable: ativo && podeReprogramar(etapa),
                    durationEditable: false,
                    extendedProps: {itemId: item.id, titulo: item.titulo},
                });
            }));
        }
        return lista;
    }, [eventos, comPrazos, processo, podeAgendar]);

    const proximos = eventos.filter(e => e.situacao === SituacaoEvento.AGENDADO).slice(0, 12);
    const passados = eventos.filter(e => e.situacao !== SituacaoEvento.AGENDADO).reverse().slice(0, 8);
    const agenda = ativo && podeAgendar;

    return (
        <div className="pr-agenda">
            <section className="pr-agenda-calendario">
                <div className="pr-agenda-topo">
                    <label className="pr-opcao pequena">
                        <input type="checkbox" checked={comPrazos} onChange={e => setComPrazos(e.target.checked)}/> mostrar prazos dos itens pendentes
                    </label>
                    {agenda && <span className="pr-ajuda pr-agenda-dica">Arraste para remarcar; na semana, selecione um horário para agendar.</span>}
                    {agenda && (
                        <CButton color="primary" size="sm" onClick={() => onAgendar({})}>
                            <CIcon name="cil-plus"/> Novo evento
                        </CButton>
                    )}
                </div>
                <Calendario
                    altura="auto"
                    eventos={noCalendario}
                    aoClicar={arg => {
                        const {evento, itemId} = arg.event.extendedProps as any;
                        if (evento) onAbrirEvento(evento);
                        if (itemId) onAbrirItem(itemId);
                    }}
                    aoSelecionar={agenda ? arg => {
                        arg.view.calendar.unselect();
                        if (arg.allDay) {
                            onAgendar({data: dataHoraApi(arg.start).slice(0, 10)});
                        } else {
                            onAgendar({inicio: dataHoraApi(arg.start), fim: dataHoraApi(arg.end)});
                        }
                    } : undefined}
                    aoMover={arg => {
                        const pedido = pedidoDoCalendario(arg.event, arg.oldEvent, arg.revert);
                        pedido ? setRemarcando(pedido) : arg.revert();
                    }}
                    aoRedimensionar={arg => {
                        const pedido = pedidoDoCalendario(arg.event, arg.oldEvent, arg.revert);
                        pedido ? setRemarcando(pedido) : arg.revert();
                    }}
                />
            </section>
            <aside className="pr-agenda-lado">
                <div className="pr-bloco-titulo">Próximos eventos</div>
                {proximos.length === 0 && <p className="pr-ajuda">Nada agendado.</p>}
                {proximos.map(evento => (
                    <button type="button" key={evento.id} className="pr-agenda-evento" onClick={() => onAbrirEvento(evento)}>
                        <span className="pr-agenda-cor" style={{background: COR_EVENTO[evento.tipo]}}/>
                        <span>
                            <strong>{evento.titulo}</strong>
                            {evento.pelo_cliente && <span className="pr-chip cliente pr-agenda-cliente">agendado pelo cliente</span>}
                            <em>{evento.tipo_nome} · {dataBr(evento.inicio)}{evento.fim ? `–${evento.fim.slice(11)}` : ''}</em>
                            {evento.item_titulo && <em className="pr-agenda-item"><CIcon name="cil-task"/> {evento.item_titulo}</em>}
                            {evento.local && <em>{evento.local}</em>}
                        </span>
                    </button>
                ))}
                {passados.length > 0 && <div className="pr-bloco-titulo mt-3">Realizados e cancelados</div>}
                {passados.map(evento => (
                    <button type="button" key={evento.id} className={`pr-agenda-evento passado sit-evento-${evento.situacao}`} onClick={() => onAbrirEvento(evento)}>
                        <span className="pr-agenda-cor" style={{background: COR_EVENTO[evento.tipo]}}/>
                        <span>
                            <strong>{evento.titulo}</strong>
                            <em>{evento.situacao_nome} · {dataBr(evento.inicio)}</em>
                        </span>
                    </button>
                ))}
            </aside>
            {remarcando && <Remarcacao pedido={remarcando} aoFechar={feito => { setRemarcando(null); if (feito) recarregar(); }}/>}
        </div>
    );
};

export default AgendaAba;
